import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../api';
import PDFViewer from './PDFViewer';
import './ExtractionReview.css';

const ExtractionReview = () => {
  const { workOrderId, extractionId } = useParams();
  const navigate = useNavigate();
  const pdfContainerRef = useRef(null);
  const [extraction, setExtraction] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('meta');
  const [reviewStatus, setReviewStatus] = useState('pending');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [draggedText, setDraggedText] = useState('');
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState('string');
  const [propertyNameError, setPropertyNameError] = useState('');
  const [dragHighlight, setDragHighlight] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const [highlightBox, setHighlightBox] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionId, workOrderId]);

  useEffect(() => {
    // Initialize edited data with extraction data
    if (extraction) {
      setEditedData({ ...extraction.extractedData || {} });
    }
  }, [extraction]);

  useEffect(() => {
    // Set up drag-drop listeners on PDF container
    const container = pdfContainerRef.current;
    if (!container) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragHighlight(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragHighlight(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragHighlight(false);

      // Get selected text (if user dragged text)
      const text = e.dataTransfer.getData('text/plain');
      if (text && text.trim()) {
        setDraggedText(text.trim());
        setShowAddPropertyModal(true);
      }
    };

    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('drop', handleDrop);

    return () => {
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('drop', handleDrop);
    };
  }, []);

  // Handle text selection for dragging
  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container) return;

    const handleMouseUp = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        // Text is selected - allow drag
      }
    };

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Get work order for context
      const woRes = await apiCall(`/api/work-orders/${workOrderId}`);
      const wo = await woRes.json();
      setWorkOrder(wo);

      // Get extraction result
      const extUrl = `/api/work-orders/${workOrderId}/extractions`;
      const extRes = await apiCall(extUrl);
      const extractions = await extRes.json();
      const ext = extractions.find(e => e._id === extractionId);

      if (!ext) throw new Error('Extraction not found');
      setExtraction(ext);
      setReviewStatus(ext.reviewStatus || 'pending');
      setReviewNotes(ext.reviewNotes || '');
      setEditedData(ext.extractedData || {});

      // Get file information for PDF viewer
      if (ext.fileId) {
        try {
          const fileRes = await apiCall(`/api/work-orders/${workOrderId}/files/${ext.fileId}`);
          if (fileRes.ok) {
            const fileData = await fileRes.json();
            setFile(fileData);
          }
        } catch (err) {
          console.log('Could not fetch file data:', err.message);
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleReview = async (status) => {
    try {
      setSubmitting(true);
      setError('');

      // Note: We'd need an API endpoint for this
      // For now, we'll just update the local state
      setReviewStatus(status);
      setSubmitting(false);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const validatePropertyName = (name) => {
    if (!name || name.trim() === '') {
      return 'Property name cannot be empty';
    }
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
      return 'Property name must start with letter or underscore, contain only alphanumeric characters and underscores';
    }
    if (name in editedData) {
      return 'Property name already exists';
    }
    return '';
  };

  const handleAddProperty = () => {
    setNewPropertyName('');
    setNewPropertyType('string');
    setPropertyNameError('');
    setDraggedText('');
    setShowAddPropertyModal(true);
  };

  const handleCreateProperty = () => {
    const validationError = validatePropertyName(newPropertyName);
    if (validationError) {
      setPropertyNameError(validationError);
      return;
    }

    // Add the new property to edited data
    setEditedData(prev => ({
      ...prev,
      [newPropertyName]: draggedText || ''
    }));

    // Close modal and reset
    setShowAddPropertyModal(false);
    setNewPropertyName('');
    setNewPropertyType('string');
    setPropertyNameError('');
    setDraggedText('');
  };

  const handleEditValue = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDeleteProperty = (key) => {
    setEditedData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  };

  const handleFieldClick = (fieldName, fieldValue) => {
    // Set the highlighted field
    setHighlightedField(fieldName);

    // Look up bounding box data
    if (extraction && extraction.bboxData && extraction.bboxData[fieldName]) {
      const bbox = extraction.bboxData[fieldName];
      setHighlightBox({
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height,
        page: bbox.page || 1,
        text: fieldName,
        confidence: bbox.confidence
      });
    } else {
      // No bbox data available
      console.log(`No bounding box data available for field: ${fieldName}`);
      setHighlightBox(null);
    }
  };

  const clearHighlight = () => {
    setHighlightedField(null);
    setHighlightBox(null);
  };

  const flattenNestedData = (obj, prefix = '') => {
    const result = [];

    const flatten = (current, key) => {
      if (current === null || current === undefined) {
        result.push([key, '']);
      } else if (typeof current === 'object' && !Array.isArray(current)) {
        const isEmpty = Object.keys(current).length === 0;
        if (isEmpty) {
          result.push([key, '{}']);
        } else {
          Object.entries(current).forEach(([nestedKey, nestedValue]) => {
            const newKey = key ? `${key}.${nestedKey}` : nestedKey;
            flatten(nestedValue, newKey);
          });
        }
      } else if (Array.isArray(current)) {
        result.push([key, JSON.stringify(current)]);
      } else {
        result.push([key, current]);
      }
    };

    Object.entries(obj).forEach(([key, value]) => {
      flatten(value, key);
    });

    return result;
  };

  const handleSaveChanges = async () => {
    try {
      setSubmitting(true);
      setError('');

      console.log('=== SAVE CHANGES DEBUG ===');
      console.log('workOrderId:', workOrderId);
      console.log('extractionId:', extractionId);
      console.log('editedData:', editedData);
      console.log('original extraction.extractedData:', extraction?.extractedData);

      // Save each changed field
      const changedFields = {};
      const originalData = extraction?.extractedData || {};

      for (const key in editedData) {
        const hasChanged = !(key in originalData) || String(originalData[key]) !== String(editedData[key]);
        if (hasChanged) {
          changedFields[key] = editedData[key];
          console.log(`Changed field: ${key} => ${editedData[key]}`);
        }
      }

      console.log('changedFields:', changedFields);

      // If no changes, just show message
      if (Object.keys(changedFields).length === 0) {
        setSubmitting(false);
        alert('No changes to save');
        console.log('No changes detected');
        return;
      }

      // Update each field via API
      for (const [fieldName, value] of Object.entries(changedFields)) {
        const url = `/api/work-orders/${workOrderId}/extractions/${extractionId}/fields/${fieldName}`;
        console.log(`Making PUT request to: ${url}`);

        const response = await apiCall(url, {
          method: 'PUT',
          body: JSON.stringify({
            value: value,
            validationStatus: 'VALID'
          })
        });

        console.log(`Response status: ${response.status}`);
        const responseData = await response.json();
        console.log(`Response data:`, responseData);

        if (!response.ok) {
          throw new Error(`Failed to update field ${fieldName}: ${responseData.error || response.statusText}`);
        }
      }

      console.log('All fields saved, refreshing data...');
      // Refresh extraction data
      await fetchData();
      setSubmitting(false);
      alert('Changes saved successfully!');
      console.log('=== SAVE COMPLETE ===');
    } catch (err) {
      console.error('Error in handleSaveChanges:', err);
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="extraction-review loading">
        <div className="spinner"></div>
        <p>Loading extraction...</p>
      </div>
    );
  }

  if (!extraction) {
    return (
      <div className="extraction-review">
        <div className="error-message">{error || 'Extraction not found'}</div>
      </div>
    );
  }

  return (
    <div className="extraction-review">
      {/* Header */}
      <div className="review-header">
        <div className="header-left">
          <button className="link" onClick={() => navigate(`/work-order/${workOrderId}`)}>
            ← Back to Work Order
          </button>
          <h1>Extraction Review</h1>
          <p>{workOrder?.reportType}</p>
        </div>
        <div className="header-right">
          <span className="status-badge" style={{ backgroundColor: '#10b981' }}>
            Extracted
          </span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Main Content */}
      <div className="review-container">
        {/* Left Panel: PDF Preview */}
        <div className="pdf-panel">
          <div className="panel-header">
            <h3>📄 Document Preview</h3>
            <p className="drag-hint">💡 Click a field to highlight it on the PDF</p>
            {highlightedField && (
              <div className="highlight-info">
                <span>📍 Highlighting: <strong>{highlightedField}</strong></span>
                <button
                  className="clear-highlight-btn"
                  onClick={clearHighlight}
                  title="Clear highlight"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          <div
            className={`pdf-viewer-container ${dragHighlight ? 'drag-active' : ''}`}
            ref={pdfContainerRef}
          >
            <PDFViewer
              highlightBox={highlightBox}
              pdfPath={file?.path}
              fileId={extraction?.fileId}
              workOrderId={workOrderId}
            />
            {dragHighlight && <div className="drag-overlay">Drop text here to create new property</div>}
          </div>
        </div>

        {/* Right Panel: Extracted Data */}
        <div className="data-panel">
          <div className="panel-header">
            <div>
              <h3>📋 Extracted Data</h3>
            </div>
            <div className="panel-tabs">
              <button
                className={`tab ${activeTab === 'meta' ? 'active' : ''}`}
                onClick={() => setActiveTab('meta')}
              >
                Meta
              </button>
              <button
                className={`tab ${activeTab === 'table' ? 'active' : ''}`}
                onClick={() => setActiveTab('table')}
              >
                Table
              </button>
              <button
                className="tab add-property-btn"
                onClick={handleAddProperty}
                title="Add new property"
              >
                + Add Property
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="data-content">
            {activeTab === 'meta' && (
              <div className="metadata-view">
                {Object.entries(editedData).length === 0 ? (
                  <div className="empty-state">
                    <p>No extracted data</p>
                    <button className="btn-link" onClick={handleAddProperty}>Add first property</button>
                  </div>
                ) : (
                  <div className="metadata-list">
                    {Object.entries(editedData).map(([key, value]) => (
                      <div
                        key={key}
                        className={`metadata-item editable ${highlightedField === key ? 'highlighted' : ''}`}
                        onClick={() => handleFieldClick(key, value)}
                        title="Click to highlight on PDF"
                      >
                        <div className="item-label">
                          {extraction?.bboxData && extraction.bboxData[key] && (
                            <span className="bbox-indicator" title="Bounding box available">📍</span>
                          )}
                          {key}
                        </div>
                        <input
                          type="text"
                          className="item-value-input"
                          value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          onChange={(e) => handleEditValue(key, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Value"
                        />
                        <div className="item-actions">
                          <button
                            className="item-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(key);
                            }}
                            title="Delete property"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'table' && (
              <div className="table-view">
                <table className="extracted-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flattenNestedData(editedData).map(([key, value]) => {
                      const depth = (key.match(/\./g) || []).length;
                      return (
                        <tr
                          key={key}
                          className={`${highlightedField === key ? 'highlighted' : ''}`}
                          data-depth={depth}
                          onClick={() => handleFieldClick(key, value)}
                          title="Click to highlight on PDF"
                        >
                          <td className="field-name">
                            {extraction?.bboxData && extraction.bboxData[key] && (
                              <span className="bbox-indicator" title="Bounding box available">📍</span>
                            )}
                            <span className="field-path">{key}</span>
                          </td>
                        <td className="value-cell">
                          <input
                            type="text"
                            className="table-value-input"
                            value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            onChange={(e) => handleEditValue(key, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="actions-cell">
                          <button
                            className="delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(key);
                            }}
                            title="Delete property"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Review Section */}
          <div className="review-section">
            <div className="review-status">
              <label>Review Status</label>
              <div className="status-buttons">
                <button
                  className={`status-btn ${reviewStatus === 'verified' ? 'active' : ''}`}
                  onClick={() => handleReview('verified')}
                  disabled={submitting}
                >
                  ✓ Verified
                </button>
                <button
                  className={`status-btn ${reviewStatus === 'rejected' ? 'active' : ''}`}
                  onClick={() => handleReview('rejected')}
                  disabled={submitting}
                >
                  ✕ Rejected
                </button>
                <button
                  className={`status-btn ${reviewStatus === 'pending' ? 'active' : ''}`}
                  onClick={() => handleReview('pending')}
                  disabled={submitting}
                >
                  ⏳ Pending
                </button>
              </div>
            </div>

            <div className="review-notes">
              <label>Review Notes</label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this extraction..."
                disabled={submitting}
              />
            </div>

            <div className="review-actions">
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/work-order/${workOrderId}`)}
              >
                Cancel
              </button>
              <div className="action-group">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveChanges}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Property Modal */}
      {showAddPropertyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Property</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddPropertyModal(false);
                  setNewPropertyName('');
                  setPropertyNameError('');
                  setDraggedText('');
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  className={`form-input ${propertyNameError ? 'error' : ''}`}
                  value={newPropertyName}
                  onChange={(e) => {
                    setNewPropertyName(e.target.value);
                    setPropertyNameError('');
                  }}
                  placeholder="e.g., customerName, dateReceived"
                  autoFocus
                />
                {propertyNameError && (
                  <span className="error-message">{propertyNameError}</span>
                )}
              </div>

              <div className="form-group">
                <label>Data Type</label>
                <select
                  className="form-input"
                  value={newPropertyType}
                  onChange={(e) => setNewPropertyType(e.target.value)}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                  <option value="array">Array</option>
                </select>
              </div>

              {draggedText && (
                <div className="form-group">
                  <label>Dragged Text Value</label>
                  <div className="dragged-text-preview">
                    <p>"{draggedText}"</p>
                  </div>
                  <p className="help-text">This value will be added to the new property</p>
                </div>
              )}

              <div className="form-group">
                <label>Initial Value (optional)</label>
                <textarea
                  className="form-input"
                  value={draggedText}
                  onChange={(e) => setDraggedText(e.target.value)}
                  placeholder="Enter initial value or leave empty"
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddPropertyModal(false);
                  setNewPropertyName('');
                  setPropertyNameError('');
                  setDraggedText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateProperty}
              >
                Create Property
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractionReview;
